/**
 * `pasteExtension` — paste handling: HTML clipboard → markdown via Turndown, plain/markdown
 * verbatim (editor-chat.md §1.2, §2.1, §5 step 5).
 *
 * editor-chat.md §1.2: paste normalizes **into markdown at the boundary** — HTML clipboard
 * content is converted to markdown via Turndown.js *the instant it enters the document*, so
 * the canonical body never contains HTML. Plain text and markdown paste insert **verbatim** —
 * the editor is a faithful text surface (no smart-quote substitution, no reformatting;
 * `chunks.hash` change detection and D-007 conflict detection depend on byte-stability).
 *
 * The decision rule (editor-chat.md §1.2):
 *  - the clipboard carries `text/html` AND it is "real" HTML (not just a plain-text fragment
 *    the OS wrapped) → convert the HTML to markdown via Turndown, insert that;
 *  - otherwise → insert `text/plain` verbatim.
 *
 * This is wired as a CM6 `EditorView.domEventHandlers` paste handler. Returning `true`
 * signals "handled — do not run CM6's default paste"; returning `false` lets the default
 * (verbatim plain-text) paste proceed, which is exactly what we want for the plain/markdown
 * case, so the handler only *intercepts* the HTML case.
 */

import { EditorView } from '@codemirror/view';
import type { Extension } from '@codemirror/state';
import TurndownService from 'turndown';
import { getLogger } from '@/api';
import type { NoteId } from '@/types';

const log = getLogger('editor.paste');

/**
 * Image-paste handling injected by the editor surface (Attachments & media track). With a note
 * to attach to, a pasted image is UPLOADED and inserted as `![pasted](attachment:<id>)`; with no
 * note (scratch buffer) or on upload failure it falls back to an inline base64 `data:` URL so the
 * paste is never lost.
 */
export interface PasteHandlers {
  /** The editor's current note id (`null` for an unsaved scratch buffer). */
  getNoteId: () => NoteId | null;
  /** Upload a pasted image's bytes; resolves to the stored attachment id. */
  uploadImage: (blob: Blob, noteId: NoteId) => Promise<string>;
}

/**
 * The Turndown instance — configured once for the whole editor (it is stateless across
 * conversions). The options favour a *canonical, stable* markdown output (editor-chat.md §1.2):
 *  - ATX headings (`#`), not Setext — one heading syntax, no ambiguity;
 *  - fenced code blocks with backticks — matches what the rest of Aletheia's markdown uses;
 *  - `*` for emphasis and `-` for bullets — a single consistent choice so re-pasting the same
 *    content yields byte-identical markdown (which matters for `chunks.hash` stability).
 */
const turndown = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  fence: '```',
  emDelimiter: '*',
  bulletListMarker: '-',
  // A pasted-in `<hr>` becomes a markdown thematic break, etc. — Turndown's defaults are sane;
  // we only pin the choices above that have more than one common form.
});

/**
 * Heuristic: does this clipboard `text/html` payload represent *real* structured HTML worth
 * converting, or is it just the OS wrapping a plain-text selection in a bare `<html>` shell?
 *
 * A real rich-text paste (from a browser, a word processor) carries structural tags. A
 * plain-text selection copied from a terminal often still arrives with a `text/html` flavour
 * that is just the text inside a `<pre>` or bare `<html><body>` — converting that adds nothing
 * and risks munging. So: only treat it as HTML-to-convert if it contains a structural tag
 * beyond the document shell.
 */
function isRichHtml(html: string): boolean {
  // Strip the document-shell tags, then look for any remaining tag.
  const withoutShell = html
    .replace(/<\/?(?:html|head|body|meta|style|title|!doctype)[^>]*>/gi, '')
    .trim();
  return /<[a-z][\s\S]*?>/i.test(withoutShell);
}

/**
 * The paste DOM event handler. Intercepts only the rich-HTML case: converts the HTML to
 * markdown via Turndown and dispatches the conversion as a normal edit transaction (so undo,
 * the dirty-state tracker, and the autosave all see it as one user edit). For plain text /
 * markdown it returns `false` and CM6's default verbatim paste runs.
 *
 * A Turndown conversion failure is a *system* error (editor-chat.md §4) — it is logged, and
 * the handler falls back to inserting the clipboard's `text/plain` verbatim rather than
 * dropping the paste (conventions.md §3 — no silent failures; never lose the user's content).
 */
function _insertBase64(view: EditorView, blob: Blob): void {
  // Fallback: inline the image as a base64 `data:` URL so the paste is never lost (scratch
  // buffer with no note, or an upload failure). Discouraged — see the Attachments & media track.
  const reader = new FileReader();
  reader.onload = () => {
    const dataUrl = String(reader.result ?? '');
    if (!dataUrl) return;
    const { from, to } = view.state.selection.main;
    const md = `\n![pasted](${dataUrl})\n`;
    view.dispatch({
      changes: { from, to, insert: md },
      selection: { anchor: from + md.length },
      userEvent: 'input.paste',
    });
  };
  reader.readAsDataURL(blob);
}

function handlePaste(
  event: ClipboardEvent,
  view: EditorView,
  handlers: PasteHandlers | undefined,
): boolean {
  const clipboard = event.clipboardData;
  if (clipboard === null) return false; // nothing to do — let the default run

  // Image paste — UPLOAD as an attachment (Attachments & media track) and insert an
  // `![pasted](attachment:<id>)` reference; fall back to inline base64 with no note / on failure.
  const imageItem = Array.from(clipboard.items).find((it) => it.type.startsWith('image/'));
  if (imageItem) {
    const blob = imageItem.getAsFile();
    if (blob) {
      event.preventDefault();
      const noteId = handlers?.getNoteId() ?? null;
      if (handlers && noteId !== null) {
        const { from, to } = view.state.selection.main;
        handlers
          .uploadImage(blob, noteId)
          .then((id) => {
            const md = `\n![pasted](attachment:${id})\n`;
            view.dispatch({
              changes: { from, to, insert: md },
              selection: { anchor: from + md.length },
              userEvent: 'input.paste',
            });
          })
          .catch((err) => {
            log.error('image_paste_upload_failed', { error: String(err) });
            _insertBase64(view, blob); // never lose the paste
          });
      } else {
        _insertBase64(view, blob); // scratch buffer — no note to attach to
      }
      return true;
    }
  }

  const html = clipboard.getData('text/html');
  const plain = clipboard.getData('text/plain');

  // Plain / markdown paste — insert verbatim. Return false so CM6's default paste handles it
  // (the default already inserts text/plain verbatim, which is exactly the faithful-text
  // behaviour editor-chat.md §1.2 wants — no reformatting, no smart quotes).
  if (html === '' || !isRichHtml(html)) {
    return false;
  }

  // Rich-HTML paste — convert to markdown at the boundary so the canonical body never holds
  // HTML (editor-chat.md §1.2).
  let markdown: string;
  try {
    markdown = turndown.turndown(html);
  } catch (err) {
    // A conversion failure is a system error — log it, fall back to verbatim plain text so the
    // user never loses the paste.
    log.error('paste_html_conversion_failed', { error: String(err), htmlLength: html.length });
    if (plain === '') return false; // nothing usable — let the default handle the empty case
    markdown = plain;
  }

  // Dispatch the conversion as a single edit transaction at the current selection — `userEvent`
  // marks it as a paste so CM6's history groups it as one undoable action and the dirty-state
  // listener treats it as one user edit.
  event.preventDefault();
  const { from, to } = view.state.selection.main;
  view.dispatch({
    changes: { from, to, insert: markdown },
    selection: { anchor: from + markdown.length },
    userEvent: 'input.paste',
  });
  log.debug('paste_html_converted', { htmlLength: html.length, markdownLength: markdown.length });
  return true; // handled — do not run CM6's default paste
}

/**
 * The paste extension — a CM6 DOM paste handler that converts rich-HTML clipboard content to
 * markdown at the boundary, lets plain/markdown paste through verbatim (editor-chat.md §1.2,
 * §2.1), and uploads pasted images as attachment references.
 *
 * A factory over the injected :type:`PasteHandlers` (the note id + image uploader). Called
 * without handlers it still converts HTML and falls back to inline base64 for images — so a
 * surface that hasn't wired uploads degrades gracefully.
 */
export function pasteExtension(handlers?: PasteHandlers): Extension {
  return EditorView.domEventHandlers({
    paste: (event, view) => handlePaste(event, view, handlers),
  });
}
